import React from 'react';
import type { TrackingVehicleType } from './pickupTrackingTypes';

/**
 * 揽收大屏专用装饰性 SVG，1:1 抠自产品原型。
 * 车身颜色 / 选中描边通过 props 注入，旋转由外层容器控制，SVG 本身不转。
 */

interface VehicleIconProps {
    /** null（非三档车型）按 van 车身画 */
    type: TrackingVehicleType | null;
    color: string;
    /** 选中时白色描边加粗，未选中半透明细描边 */
    selected?: boolean;
}

const strokeOf = (selected: boolean) => ({
    stroke: selected ? '#ffffff' : 'rgba(2,18,34,.55)',
    strokeWidth: selected ? 2.4 : 1.2,
});

const Van: React.FC<{ color: string; selected: boolean }> = ({ color, selected }) => {
    const { stroke, strokeWidth } = strokeOf(selected);
    return (
        <svg width="34" height="46" viewBox="0 0 34 46" aria-hidden="true">
            <ellipse cx="17" cy="23" rx="16" ry="22" fill="rgba(2,14,28,.42)" />
            <rect x="5" y="3" width="24" height="40" rx="9.5" fill={color} stroke={stroke} strokeWidth={strokeWidth} />
            <rect x="1.6" y="16" width="3.4" height="6" rx="1.6" fill={color} />
            <rect x="29" y="16" width="3.4" height="6" rx="1.6" fill={color} />
            <path
                d="M9 14.5c1.4-3 3.6-4.6 8-4.6s6.6 1.6 8 4.6c-5.2-1.5-10.8-1.5-16 0z"
                fill="rgba(2,18,34,.72)"
            />
            <rect x="10" y="17.5" width="14" height="21" rx="2.4" fill="rgba(255,255,255,.22)" />
            <rect x="8.4" y="4.4" width="4" height="2.6" rx="1.3" fill="rgba(255,255,255,.75)" />
            <rect x="21.6" y="4.4" width="4" height="2.6" rx="1.3" fill="rgba(255,255,255,.75)" />
        </svg>
    );
};

const Truck26: React.FC<{ color: string; selected: boolean }> = ({ color, selected }) => {
    const { stroke, strokeWidth } = strokeOf(selected);
    return (
        <svg width="34" height="56" viewBox="0 0 34 56" aria-hidden="true">
            <ellipse cx="17" cy="28" rx="16" ry="27" fill="rgba(2,14,28,.42)" />
            <rect x="6" y="3" width="22" height="14" rx="5" fill={color} stroke={stroke} strokeWidth={strokeWidth} />
            <path
                d="M9.5 10.5c1.2-2.4 3-3.6 7.5-3.6s6.3 1.2 7.5 3.6c-4.8-1.2-10.2-1.2-15 0z"
                fill="rgba(2,18,34,.72)"
            />
            <rect x="8.4" y="4.2" width="3.6" height="2.4" rx="1.2" fill="rgba(255,255,255,.75)" />
            <rect x="22" y="4.2" width="3.6" height="2.4" rx="1.2" fill="rgba(255,255,255,.75)" />
            <rect x="4" y="19" width="26" height="34" rx="2.5" fill={color} stroke={stroke} strokeWidth={strokeWidth} />
            <rect x="8" y="23" width="18" height="26" rx="1.5" fill="rgba(2,18,34,.28)" />
            <path d="M17 26v20M11 26v20M23 26v20" stroke="rgba(255,255,255,.28)" strokeWidth="1.2" />
        </svg>
    );
};

const Truck53: React.FC<{ color: string; selected: boolean }> = ({ color, selected }) => {
    const { stroke, strokeWidth } = strokeOf(selected);
    return (
        <svg width="34" height="66" viewBox="0 0 34 66" aria-hidden="true">
            <ellipse cx="17" cy="33" rx="16" ry="32" fill="rgba(2,14,28,.42)" />
            <rect x="7" y="3" width="20" height="13" rx="5" fill={color} stroke={stroke} strokeWidth={strokeWidth} />
            <path d="M10 10c1.1-2.2 2.8-3.3 7-3.3s5.9 1.1 7 3.3c-4.5-1.1-9.5-1.1-14 0z" fill="rgba(2,18,34,.72)" />
            <rect x="9" y="4.2" width="3.4" height="2.2" rx="1.1" fill="rgba(255,255,255,.75)" />
            <rect x="21.6" y="4.2" width="3.4" height="2.2" rx="1.1" fill="rgba(255,255,255,.75)" />
            <rect x="15.5" y="16" width="3" height="3" fill="rgba(2,18,34,.8)" />
            <rect x="3" y="19" width="28" height="44" rx="2" fill={color} stroke={stroke} strokeWidth={strokeWidth} />
            <rect x="6.5" y="22.5" width="21" height="37" rx="1" fill="rgba(2,18,34,.30)" />
            <path d="M6.5 31h21M6.5 40h21M6.5 49h21" stroke="rgba(255,255,255,.26)" strokeWidth="1.2" />
        </svg>
    );
};

/** 车型俯视图：van 34×46 / 26 尺 34×56 / 53 尺 34×66 */
export const VehicleIcon: React.FC<VehicleIconProps> = ({ type, color, selected = false }) => {
    if (type === 't26') return <Truck26 color={color} selected={selected} />;
    if (type === 't53') return <Truck53 color={color} selected={selected} />;
    return <Van color={color} selected={selected} />;
};

/** 皇冠：成功率最高的司机头顶挂一顶。26×20，金色 `#ffd54a` */
export const CrownIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} width="26" height="20" viewBox="0 0 26 20" aria-hidden="true">
        <path
            d="M2.5 15.5 1 4.6l6.2 4.1L13 1.6l5.8 7.1L25 4.6l-1.5 10.9z"
            fill="#ffd54a"
            stroke="#5c3c00"
            strokeWidth="1.1"
            strokeLinejoin="round"
        />
        <rect x="2.4" y="16.4" width="21.2" height="2.6" rx="1.3" fill="#ffd54a" stroke="#5c3c00" strokeWidth="1" />
    </svg>
);

/** HUB 标记：56×66 的针形房子，白底青描边 */
export const HubPinIcon: React.FC = () => (
    <svg
        width="56"
        height="66"
        viewBox="0 0 56 66"
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,.65))' }}
    >
        <path
            d="M28 64C28 64 51 37 51 24.5A23 23 0 1 0 5 24.5C5 37 28 64 28 64Z"
            fill="#e2e8f0"
            stroke="#22d3ee"
            strokeWidth="2.6"
        />
        <g fill="none" stroke="#0b3a5c" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round">
            <path d="M16 25.5L28 15l12 10.5" />
            <path d="M19 25v11h18V25" />
            <path d="M25 36v-6.5h6V36" />
        </g>
    </svg>
);
